/**
 * BAM Film Studio data layer. Thin wrappers around Supabase queries
 * for films, film_notes, film_collections, and film_collection_items.
 *
 * Conventions:
 *   - All queries are scoped via RLS to auth.uid() — no need to filter
 *     by user_id in JS.
 *   - Functions throw on error so callers can surface to the UI.
 *   - Returns plain JS objects matching the schema.
 *
 * Provider detection lives in `detectProvider()` so it's easy to
 * extend (Veo / Pixellot / Hudl share-link parsing in V2).
 */
import { supabase } from "../../supabaseClient";
import { authHeaders } from "../../utils/authHeaders";

// ── Web Locks resilience ─────────────────────────────────────────────
//
// Supabase JS v2 uses navigator.locks for cross-tab session sync. When
// another tab/process steals the lock mid-query (via {steal: true},
// which is what supabase does on session refresh contention), the
// in-flight fetch rejects with:
//   AbortError: Lock broken by another request with the 'steal' option.
//
// The query itself isn't actually broken — the underlying data is
// fine. We just need to retry once the new lock holder releases. This
// helper transparently retries any thrown lock error with a short
// backoff so the FilmWatch / FilmLibrary UIs never show that internal
// error to the user.
async function withLockRetry(fn, { maxRetries = 3, baseDelayMs = 200 } = {}) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (e) {
      const msg = String(e?.message || e || "");
      const isLock = (e?.name === "AbortError" || msg.includes("Lock broken") || msg.includes("lock request"));
      if (!isLock || attempt >= maxRetries) throw e;
      attempt += 1;
      await new Promise((r) => setTimeout(r, baseDelayMs * (attempt + 1)));
    }
  }
}

// ── Provider detection + URL helpers ─────────────────────────────────

/**
 * Sniff the provider from a pasted URL. V1 supports YouTube only on
 * the embed path (per Coleman's call); IG/TikTok/Vimeo are accepted
 * by the schema but we don't have player components for them yet —
 * they'll show a generic "open in source" link.
 */
export function detectProvider(url) {
  if (!url || typeof url !== "string") return "other";
  const u = url.trim().toLowerCase();
  if (/youtube\.com|youtu\.be/.test(u)) return "youtube";
  if (/instagram\.com/.test(u)) return "instagram";
  if (/tiktok\.com/.test(u)) return "tiktok";
  if (/vimeo\.com/.test(u)) return "vimeo";
  return "other";
}

export function extractYouTubeId(url) {
  if (!url) return null;
  // Handles: youtube.com/watch?v=XXX, youtu.be/XXX, youtube.com/shorts/XXX,
  // youtube.com/embed/XXX, with optional query params + fragments.
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

/**
 * YouTube thumbnail URL for a video ID. Used as the cached
 * thumbnail_url on the film record so the library shows a
 * preview without loading the player.
 */
export function youtubeThumbnail(videoId, quality = "hqdefault") {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * MM:SS or H:MM:SS formatter for displaying note timestamps.
 */
export function formatTime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// ── Films ────────────────────────────────────────────────────────────

export async function listFilms({ kind } = {}) {
  return withLockRetry(async () => {
    let q = supabase
      .from("films")
      .select("*")
      .order("created_at", { ascending: false });
    if (kind && kind !== "all") q = q.eq("kind", kind);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  });
}

// ── Advantage Flow's games stay out of Film Studio ──────────────────
//
// A game added for Advantage Flow (#/afs) is a films row too, but it isn't
// something to watch in Film Studio: AFS has its own game screen. Film
// Studio's lists (Next Up, Recently Added, the rails, collections) leave out
// any film AFS has a session on, and any film AFS made itself. AFS still sees
// every game, and nothing is deleted. A film added in Film Studio and never
// tagged stays in Film Studio.

/** meta.origin on a film Advantage Flow created (its Add a game, the Synergy import). */
export const AFS_ORIGIN = "afs";

/**
 * Whether a film belongs to Advantage Flow: it has a session (its id is in
 * afsFilmIds), or AFS made it (meta.origin "afs", or one of the older
 * AFS-only marks: a clips game's meta.reel, a done-for-you copy, an Academy
 * copy).
 */
export function isAfsFilm(film, afsFilmIds) {
  if (!film) return false;
  if (afsFilmIds && afsFilmIds.has(film.id)) return true;
  const m = (film.meta && typeof film.meta === "object") ? film.meta : {};
  return m.origin === AFS_ORIGIN || !!m.reel || !!m.afs_dfy_of || !!m.afs_academy;
}

/** meta.origin on a clip uploaded from ShotFlow (src/dashboard/lab/ShotflowLab.jsx). */
export const SHOTFLOW_ORIGIN = "shotflow";
/**
 * A ShotFlow shooting clip (Coleman, 2026-11-03: "shotflow clips shouldnt have gone in there"): marked shotflow, or one
 * uploaded before the mark, a workout described as a ShotFlow clip. They live in ShotFlow, not Watch Film.
 */
export function isShotflowClip(film) {
  if (!film) return false;
  const m = (film.meta && typeof film.meta === "object") ? film.meta : {};
  return m.origin === SHOTFLOW_ORIGIN || (film.kind === "workout" && /^ShotFlow shooting clip$/i.test(String(film.description || "").trim()));
}

/** The films Film Studio shows: all of them but Advantage Flow's and ShotFlow's. */
export function withoutAfsFilms(films, afsFilmIds) {
  return (films || []).filter(f => !isAfsFilm(f, afsFilmIds) && !isShotflowClip(f));
}

/**
 * The ids of the films this account has an Advantage Flow session on, as a
 * Set. One query, film_id only (a session's possessions are large). On an
 * error it's empty, so Film Studio still opens; meta.origin still holds.
 */
export async function listAfsFilmIds() {
  try {
    return await withLockRetry(async () => {
      const { data, error } = await supabase.from("film_afs_sessions").select("film_id");
      if (error) throw error;
      return new Set((data || []).map(r => r.film_id).filter(Boolean));
    });
  } catch {
    return new Set();
  }
}

/** Film Studio's library: listFilms without Advantage Flow's games. Two queries, side by side. */
export async function listStudioFilms(opts) {
  const [films, afsFilmIds] = await Promise.all([listFilms(opts), listAfsFilmIds()]);
  return withoutAfsFilms(films, afsFilmIds);
}

export async function getFilm(id) {
  return withLockRetry(async () => {
    const { data, error } = await supabase
      .from("films")
      .select("*")
      .eq("id", id)
      .limit(1);
    if (error) throw error;
    return data?.[0] || null;
  });
}

/**
 * Create a film from a pasted URL. Auto-detects provider, extracts
 * YouTube ID + thumbnail when applicable. Returns the inserted row.
 */
export async function createFilmFromUrl({ url, title, kind, description, meta }) {
  const provider = detectProvider(url);
  let thumbnail_url = null;
  if (provider === "youtube") {
    const ytId = extractYouTubeId(url);
    if (ytId) thumbnail_url = youtubeThumbnail(ytId);
  }
  // The session already in the browser, not getUser(): that one goes to the network and can
  // hang (many open tabs contending for the auth lock), leaving "Saving…" on screen forever.
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) throw new Error("You're signed out. Refresh and try again.");

  const { data, error } = await supabase
    .from("films")
    .insert({
      user_id: user.id,
      kind: kind || "general",
      title: title || "Untitled film",
      description: description || null,
      source_kind: "embed",
      provider,
      external_url: url,
      thumbnail_url,
      meta: meta || {},
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Probe a video file's duration in seconds using HTMLVideoElement
 * metadata. Used pre-flight so the upload endpoint can short-circuit
 * before we burn Mux ingest minutes on something that would push the
 * coach past their 50-hour quota.
 *
 * Resolves with 0 (rather than rejecting) on any failure — the server
 * will still reject the upload after the fact if it really does exceed,
 * we just lose the early-exit. Some browsers also can't read duration
 * for codecs they don't natively decode (rare for modern uploads).
 */
export function probeVideoDuration(file) {
  return new Promise(resolve => {
    if (!file) return resolve(0);
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    const cleanup = () => { URL.revokeObjectURL(url); v.remove(); };
    const timer = setTimeout(() => { cleanup(); resolve(0); }, 5000);
    v.onloadedmetadata = () => {
      clearTimeout(timer);
      const d = Number(v.duration) || 0;
      cleanup();
      resolve(d);
    };
    v.onerror = () => { clearTimeout(timer); cleanup(); resolve(0); };
    v.src = url;
  });
}

/**
 * Direct-upload flow for Film Studio+ subscribers. Drives the whole
 * client-side dance:
 *   1. Probe duration so we can pre-flight quota-check on the server.
 *   2. POST /api/film-create-upload with the user's auth token →
 *      gets back { filmId, uploadId, url }.
 *   3. PUT the file to Mux's signed URL with progress reporting.
 *   4. Poll /api/mux-asset-status?uploadId=... until the asset is
 *      ready (or we time out). Server will also stamp the films row
 *      via the webhook, so once mux_status='ready' on the film we're
 *      good to navigate.
 *
 * onProgress(0..100) for the upload phase (preparing → 5%, upload
 * 5-90%, processing 90-99%, ready 100%).
 *
 * Throws on any failure with a user-friendly message.
 */
export async function createFilmUpload({ file, title, kind, description, origin, onProgress }) {
  if (!file) throw new Error("Pick a video file first.");
  const reportProgress = (n) => { try { onProgress?.(Math.max(0, Math.min(100, Math.round(n)))); } catch {} };
  reportProgress(2);

  const estimatedDurationSeconds = await probeVideoDuration(file);
  reportProgress(5);

  // Auth token for the API. Service-role on the server validates it,
  // so we don't need to pass user_id explicitly.
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("You're signed out. Refresh and try again.");

  // 1. Create the upload + films row.
  const createRes = await fetch("/api/film-create-upload", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    // origin: "afs" from Advantage Flow's own Add a game, so Film Studio leaves it out.
    body: JSON.stringify({ title, kind, description, estimatedDurationSeconds, ...(origin ? { origin } : {}) }),
  });
  const createJson = await createRes.json().catch(() => ({}));
  if (!createRes.ok) {
    throw new Error(createJson?.error || `Upload could not be created (${createRes.status}).`);
  }
  const { filmId, uploadId, url: muxUrl } = createJson;
  if (!filmId || !uploadId || !muxUrl) throw new Error("Upload response was malformed.");

  // 2. PUT the file to Mux. Use XHR for progress events (fetch's
  //    streaming-upload story is patchy across browsers).
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", muxUrl);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        // Map 0..100% upload to 5..90% overall progress.
        reportProgress(5 + (e.loaded / e.total) * 85);
      }
    };
    xhr.onload = () => {
      // Mux returns 200/204 on success.
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.onabort = () => reject(new Error("Upload aborted."));
    xhr.send(file);
  });
  reportProgress(92);

  // 3. Poll for asset.ready. Mux usually takes 5-30s for a 1-5min
  //    clip; we'll poll for up to 5 minutes for safety on long uploads.
  //    The webhook will have stamped mux_status='ready' on the film
  //    by the time we see asset.ready here.
  const startedAt = Date.now();
  const POLL_TIMEOUT_MS = 5 * 60 * 1000;
  const POLL_INTERVAL_MS = 3000;
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const statusRes = await fetch(`/api/mux-asset-status?uploadId=${encodeURIComponent(uploadId)}`, { headers: await authHeaders() });
      const statusJson = await statusRes.json().catch(() => ({}));
      if (statusJson?.assetStatus === "ready" && statusJson?.playbackId) {
        reportProgress(100);
        // Re-read the film row so the caller has fresh fields
        // (mux_playback_id, duration_seconds, thumbnail_url) the
        // webhook just wrote.
        const film = await getFilm(filmId).catch(() => ({ id: filmId }));
        return film;
      }
      if (statusJson?.assetStatus === "errored" || statusJson?.uploadStatus === "errored") {
        throw new Error(statusJson.error || "Mux couldn't process this video.");
      }
    } catch (e) {
      // Network blip — keep polling unless we've been at it too long.
      if (Date.now() - startedAt > POLL_TIMEOUT_MS - POLL_INTERVAL_MS) throw e;
    }
  }
  // Timed out waiting for ready — but the upload itself succeeded, so
  // return the film id; the library will refresh and show pending state.
  reportProgress(99);
  return { id: filmId, mux_status: "pending" };
}

/**
 * Import a film from a pasted link straight into Mux, so it becomes
 * frame-readable and can be tagged with the AFS Co-pilot (unlike
 * createFilmFromUrl, which makes a non-readable `embed` player row).
 *
 * Direct links (Hudl/Drive/Dropbox exports, .mp4) are pulled by Mux
 * instantly; YouTube is resolved server-side (best-effort) and, when a
 * Modal worker is deployed, falls back to a yt-dlp download for reliability.
 *
 * Returns { filmId, mode } immediately — the asset finishes processing in
 * the background (Mux webhook flips the films row to ready), so the library
 * shows it as pending, then ready. A full game can take a few minutes.
 * Throws with a user-friendly message on failure.
 */
export async function importFilmFromUrl({ url, title, kind, description }) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("You're signed out. Refresh and try again.");

  const res = await fetch("/api/film-import-url", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ url, title, kind, description }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `Import failed (${res.status}).`);
  return json; // { filmId, mode }
}

export async function updateFilm(id, patch) {
  const allowed = {};
  for (const k of ["title", "description", "kind", "meta", "thumbnail_url", "duration_seconds"]) {
    if (k in patch) allowed[k] = patch[k];
  }
  const { data, error } = await supabase
    .from("films")
    .update(allowed)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteFilm(id) {
  const { error } = await supabase.from("films").delete().eq("id", id);
  if (error) throw error;
}

// ── Notes ────────────────────────────────────────────────────────────

export async function listNotesForFilm(filmId) {
  return withLockRetry(async () => {
    const { data, error } = await supabase
      .from("film_notes")
      .select("*")
      .eq("film_id", filmId)
      .order("timestamp_seconds", { ascending: true });
    if (error) throw error;
    return data || [];
  });
}

export async function listAllNotes({ limit = 200 } = {}) {
  return withLockRetry(async () => {
    const { data, error } = await supabase
      .from("film_notes")
      .select("*, film:films(id, title, kind, thumbnail_url, provider, external_url)")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data || [];
  });
}

export async function createNote({ filmId, timestamp, title, body, tags, drillId, timestampEnd, audioUrl, audioDurationSeconds, imageUrl, possessionPhase, possessionOutcome }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  const { data, error } = await supabase
    .from("film_notes")
    .insert({
      film_id: filmId,
      user_id: user.id,
      timestamp_seconds: Math.max(0, Number(timestamp) || 0),
      timestamp_end_seconds: timestampEnd != null ? Math.max(0, Number(timestampEnd) || 0) : null,
      title: title ? String(title).trim() : null,
      body: String(body || "").trim(),
      tags: Array.isArray(tags) ? tags : [],
      drill_id: drillId || null,
      audio_url: audioUrl || null,
      audio_duration_seconds: audioDurationSeconds != null ? Number(audioDurationSeconds) : null,
      image_url: imageUrl || null,
      possession_phase: possessionPhase || null,
      possession_outcome: possessionOutcome || null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

// Upload a recorded audio blob to the film-audio-notes bucket. Path is
// scoped under the user's UUID so the storage RLS policy gates writes
// to the actual recorder. Returns the public URL.
export async function uploadFilmAudio({ filmId, blob, ext = "webm" }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  const path = `${user.id}/${filmId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from("film-audio-notes")
    .upload(path, blob, { contentType: blob.type || "audio/webm", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from("film-audio-notes").getPublicUrl(path);
  return { url: data.publicUrl, path };
}

// Build a community-post embed payload from a film + film_note. The
// payload is self-contained so the feed renders without a follow-up
// fetch — duplicating the snapshot/audio URLs and youtube id at
// share-time means the embed survives even if the source note is
// later deleted.
export function buildFilmClipEmbed(film, note) {
  const ytId = film.provider === "youtube" ? extractYouTubeId(film.external_url) : null;
  return {
    type: "film_clip",
    youtube_url: film.external_url || null,
    youtube_id: ytId,
    thumbnail_url: film.thumbnail_url || null,
    timestamp: note.timestamp_seconds || 0,
    title: note.title || note.body || film.title || "Clip",
    body: note.title && note.body ? note.body : "",
    snapshot_url: note.image_url || null,
    audio_url: note.audio_url || null,
    source_film_kind: film.kind || "general",
    source_note_id: note.id || null,
  };
}

// Upload a snapshot PNG (frame capture or manual upload, with any
// drawing baked in by the editor canvas) to the film-snapshots bucket.
// Same RLS shape as film-audio-notes — paths live under the user's UUID.
export async function uploadFilmSnapshot({ filmId, blob, ext = "png" }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  const path = `${user.id}/${filmId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from("film-snapshots")
    .upload(path, blob, { contentType: blob.type || "image/png", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from("film-snapshots").getPublicUrl(path);
  return { url: data.publicUrl, path };
}

export async function updateNote(id, patch) {
  const allowed = {};
  for (const k of ["body", "tags", "timestamp_seconds", "timestamp_end_seconds", "drill_id"]) {
    if (k in patch) allowed[k] = patch[k];
  }
  const { data, error } = await supabase
    .from("film_notes")
    .update(allowed)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteNote(id) {
  const { error } = await supabase.from("film_notes").delete().eq("id", id);
  if (error) throw error;
}

// ── Collections ──────────────────────────────────────────────────────

export async function listCollections() {
  const { data, error } = await supabase
    .from("film_collections")
    .select("*, item_count:film_collection_items(count)")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  // Flatten the count aggregate ([{count: N}] → N).
  return (data || []).map(c => ({
    ...c,
    item_count: Array.isArray(c.item_count) ? (c.item_count[0]?.count || 0) : 0,
  }));
}

export async function getCollection(id) {
  const { data, error } = await supabase
    .from("film_collections")
    .select("*")
    .eq("id", id)
    .limit(1);
  if (error) throw error;
  return data?.[0] || null;
}

export async function listCollectionItems(collectionId) {
  // Pull items + their notes + the parent film for each. One query so
  // the collection page can render every card without N+1 fetches.
  const { data, error } = await supabase
    .from("film_collection_items")
    .select(`
      id, position, added_at,
      note:film_notes(
        id, body, tags, timestamp_seconds, timestamp_end_seconds, created_at,
        film:films(id, title, kind, thumbnail_url, provider, external_url, mux_playback_id, meta)
      )
    `)
    .eq("collection_id", collectionId)
    .order("position", { ascending: true });
  if (error) throw error;
  return data || [];
}

/** A collection's items in Film Studio: without the clips from Advantage Flow's games. */
export async function listStudioCollectionItems(collectionId) {
  const [items, afsFilmIds] = await Promise.all([listCollectionItems(collectionId), listAfsFilmIds()]);
  return (items || []).filter(it => !isAfsFilm(it?.note?.film, afsFilmIds));
}

export async function createCollection({ name, description, tags, coverFilmId }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  const { data, error } = await supabase
    .from("film_collections")
    .insert({
      user_id: user.id,
      name: String(name || "Untitled collection").trim(),
      description: description || null,
      tags: Array.isArray(tags) ? tags : [],
      cover_film_id: coverFilmId || null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function updateCollection(id, patch) {
  const allowed = {};
  for (const k of ["name", "description", "tags", "cover_film_id"]) {
    if (k in patch) allowed[k] = patch[k];
  }
  const { data, error } = await supabase
    .from("film_collections")
    .update(allowed)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCollection(id) {
  const { error } = await supabase.from("film_collections").delete().eq("id", id);
  if (error) throw error;
}

export async function addNoteToCollection({ collectionId, noteId, position }) {
  // Idempotent — the (collection_id, note_id) unique constraint stops
  // duplicates. We use upsert with ignoreDuplicates so a re-add is a
  // no-op rather than an error.
  const { data, error } = await supabase
    .from("film_collection_items")
    .upsert(
      { collection_id: collectionId, note_id: noteId, position: position || 0 },
      { onConflict: "collection_id,note_id", ignoreDuplicates: true }
    )
    .select("*");
  if (error) throw error;
  return data?.[0] || null;
}

export async function removeFromCollection({ collectionId, noteId }) {
  const { error } = await supabase
    .from("film_collection_items")
    .delete()
    .eq("collection_id", collectionId)
    .eq("note_id", noteId);
  if (error) throw error;
}
